import { OrgScopedRow } from "../common/types";
import { TradeCode } from "../template/trades";

export const CUSTOMER_STAGES = [
  "NEW",
  "CONTACTED",
  "QUOTED",
  "WON",
  "LOST",
] as const;
export type CustomerStage = (typeof CUSTOMER_STAGES)[number];

export const CUSTOMER_STAGE_LABEL: Record<CustomerStage, string> = {
  NEW: "신규",
  CONTACTED: "상담",
  QUOTED: "견적",
  WON: "수주",
  LOST: "이탈",
};

/**
 * 아래 세 유니온(`Residence`·`AreaBand`·`SpaceState`)은 **내장 기본 코드**다 —
 * 조직이 설정에서 아무것도 고치지 않았을 때 쓰는 목록이자, 지우지 못하는 바닥이다.
 * 고객 행에 저장되는 값은 조직이 추가한 코드일 수 있어 열 타입은 `string` 이고,
 * 무엇이 들어올 수 있는지는 조직의 코드값 목록(`modules/org/codes`)이 정한다.
 */
export const RESIDENCES = [
  "APT",
  "VILLA",
  "HOUSE",
  "OFFICETEL",
  "COMMERCIAL",
  "ETC",
] as const;
export type Residence = (typeof RESIDENCES)[number];

export const RESIDENCE_LABEL: Record<Residence, string> = {
  APT: "아파트",
  VILLA: "빌라·다세대",
  HOUSE: "단독주택",
  OFFICETEL: "오피스텔",
  COMMERCIAL: "상가·사무실",
  ETC: "기타",
};

export const AREA_BANDS = [
  "BAND_10",
  "BAND_20",
  "BAND_30",
  "BAND_40",
  "BAND_50",
  "BAND_60_PLUS",
] as const;
export type AreaBand = (typeof AREA_BANDS)[number];

/**
 * 내장 기본 코드의 표기. **회사가 고친 표기는 여기 없다** — 화면에 붙이는 말은
 * `resolveLabel(codes, code)` 가 정한다.
 *
 * 문자열 색인(`Record<string, string>`)을 열어 두지 않는다 — 열어 두면 모르는 코드로
 * 읽어도 타입이 `string` 이라 `undefined` 가 화면까지 그대로 나간다. 내장 코드만
 * 색인할 수 있게 두고, 그 밖의 값은 컴파일러가 막는다.
 */
export const AREA_BAND_LABEL: Record<AreaBand, string> = {
  BAND_10: "10평대",
  BAND_20: "20평대",
  BAND_30: "30평대",
  BAND_40: "40평대",
  BAND_50: "50평대",
  BAND_60_PLUS: "60평대 이상",
};

export const SPACE_STATES = ["EMPTY", "OCCUPIED", "MOVING_OUT"] as const;
export type SpaceState = (typeof SPACE_STATES)[number];

export const SPACE_STATE_LABEL: Record<SpaceState, string> = {
  EMPTY: "공실",
  OCCUPIED: "거주 중",
  MOVING_OUT: "이사 예정",
};

// 간편접수 상세 모드 세 칸(`0064`) — 회사 코드값이 아니라 앱 상수 + DB check 다.
export const WORK_SCOPES = ["FULL", "PARTIAL", "REPAIR"] as const;
export type WorkScope = (typeof WORK_SCOPES)[number];

export const WORK_SCOPE_LABEL: Record<WorkScope, string> = {
  FULL: "전체 리모델링",
  PARTIAL: "부분 공사",
  REPAIR: "보수·수리",
};

export const BUDGET_BANDS = ["UNDER_20M", "20M_40M", "40M_60M", "60M_100M", "OVER_100M"] as const;
export type BudgetBand = (typeof BUDGET_BANDS)[number];

export const BUDGET_BAND_LABEL: Record<BudgetBand, string> = {
  UNDER_20M: "2천만원 미만",
  "20M_40M": "2천~4천만원",
  "40M_60M": "4천~6천만원",
  "60M_100M": "6천만원~1억원",
  OVER_100M: "1억원 이상",
};

export const CALL_TIMES = ["MORNING", "AFTERNOON", "EVENING", "ANYTIME"] as const;
export type CallTime = (typeof CALL_TIMES)[number];

export const CALL_TIME_LABEL: Record<CallTime, string> = {
  MORNING: "오전 (9~12시)",
  AFTERNOON: "오후 (12~18시)",
  EVENING: "저녁 (18시 이후)",
  ANYTIME: "언제든 괜찮아요",
};

// 상세 모드 세 칸 — 모르면 전부 null.
export interface CustomerIntakeDetail {
  work_scope: WorkScope | null;
  budget_band: BudgetBand | null;
  call_time: CallTime | null;
}

export const CUSTOMER_SOURCES = [
  "PHONE",
  "VISIT",
  "ONLINE",
  "REFERRAL",
  "OHOU",
  "SOOMGO",
  "ZIPDOC",
  "PORTAL",
  "INTAKE",
  "NOTICE",
  "PAGE",
  "ETC",
] as const;
export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];

export const CUSTOMER_SOURCE_LABEL: Record<CustomerSource, string> = {
  PHONE: "전화",
  VISIT: "방문",
  ONLINE: "홈페이지",
  REFERRAL: "소개",
  OHOU: "오늘의집",
  SOOMGO: "숨고",
  ZIPDOC: "집닥",
  // 이름 붙은 세 플랫폼 밖의 중개 앱·포털 — 0039 이전에 들어온 플랫폼 유입도 여기 남는다.
  PORTAL: "기타 플랫폼",
  INTAKE: "간편 접수",
  // 공사 안내문 QR 로 들어온 이웃 — 어느 현장인지는 source_project_id 에 남는다.
  NOTICE: "공사 안내문",
  // 공개 시공사례 페이지의 상담 문의 — 어느 링크·사례인지는 source_public_page_id · source_portfolio_id 에 남는다.
  PAGE: "공개 페이지",
  ETC: "기타",
};

/**
 * 접수 경로. 손으로 채우는 등록 폼이 아니라 **간편 접수 화면과 홈페이지 폼 웹훅**이
 * 쓰는 좁은 입구다.
 */
export const INTAKE_SOURCES = ["intake", "site_form", "notice", "page", "referral"] as const;
export type IntakeSource = (typeof INTAKE_SOURCES)[number];

/**
 * 접수 경로가 앉는 자리. `(org_id, source, external_id)` 중복 판정의 `source` 가
 * 이 값이라, 두 경로가 같은 칸에 앉으면 서로의 external_id 를 중복으로 본다.
 */
export const INTAKE_CUSTOMER_SOURCE: Record<IntakeSource, CustomerSource> = {
  // 간편 접수는 전화가 아니다 — 고객이 스스로 폼을 채운 것이라 자리를 따로 둔다.
  intake: "INTAKE",
  site_form: "ONLINE",
  notice: "NOTICE",
  page: "PAGE",
  referral: "REFERRAL",
};

// 접수 기록 한 줄의 앞머리 — 인메모리·Supabase 가 같은 문구를 쓴다.
export const INTAKE_ACTIVITY_LABEL: Record<IntakeSource, string> = {
  intake: "간편",
  site_form: "홈페이지 폼",
  notice: "공사 안내문",
  page: "공개 페이지",
  referral: "소개 링크",
};

/** 접수 입력. 이름·연락처만 필수 — 나머지는 상담하면서 채운다. */
export interface IntakeInput {
  name: string;
  phone: string;
  site_address?: string;
  /**
   * 접수의 입구는 **내장 기본 코드만** 받는다. 남의 서버(홈페이지 폼)가 넣는 값이라
   * 회사 코드값까지 열어 두면 무엇이 들어올지 밖에서 정하게 된다.
   */
  residence?: Residence | null;
  area_band?: AreaBand | null;
  memo?: string;
  source: IntakeSource;
  /** 외부 폼의 제출 id. 있으면 같은 값으로 두 번 들어와도 한 명이다. */
  external_id?: string | null;
  meta?: Record<string, string>;
  // 고객이 고른 담당 — 부르는 쪽이 같은 조직·노출·설정을 검증한 뒤에만 넣는다.
  chosen_assignee?: { id: string; name: string } | null;
  // 공사 안내문 QR 의 현장 — 부르는 쪽이 같은 조직 현장인지 확인한 뒤에만 넣는다.
  source_project_id?: string | null;
  // 공개 페이지 유입의 링크·사례 — 저장소가 같은 조직 것만 남긴다.
  source_public_page_id?: string | null;
  source_portfolio_id?: string | null;
  // 소개 링크의 고객 — 저장소가 같은 조직 고객만 남긴다.
  referred_by_customer_id?: string | null;
  // 주소 검색으로 고른 값 — 부르는 쪽(`readAddressGroup`)이 형식을 거른 뒤에만 넣는다.
  address?: CustomerAddress;
  // 상세 모드 세 칸 — 부르는 쪽(`readIntakeDetail`)이 모드·값을 거른 뒤에만 넣는다.
  detail?: CustomerIntakeDetail;
}

// 다음 우편번호 검색 결과 — 손으로 친 주소면 전부 null(`0063`).
export interface CustomerAddress {
  address_road: string | null;
  address_jibun: string | null;
  address_zonecode: string | null;
  address_bcode: string | null;
  address_building_code: string | null;
  address_detail: string | null;
}

/** 상담 시 받아 적는 공종별 요구. 견적 초안의 공종 선택에 그대로 쓰인다. */
export interface ConsultNeed {
  trade: TradeCode;
  detail: string;
  note: string;
}

export interface Customer extends OrgScopedRow, CustomerAddress, CustomerIntakeDetail {
  name: string;
  phone: string;
  site_address: string;
  /** 조직 코드값의 `code`. 표기는 `resolveLabel` 이 붙인다. */
  residence: string | null;
  area_band: string | null;
  space_state: string | null;
  /** 확장 필요 여부. 모르면 null. */
  need_extension: boolean | null;
  /** YYYY-MM-DD. */
  move_in_hope: string | null;
  work_start_hope: string | null;
  survey_date: string | null;
  /** 원 단위 정수. */
  budget: number | null;
  source: CustomerSource | null;
  /** 외부 유입(포털·광고)의 원본 식별자. 중복 유입 판정에 쓴다. */
  external_id: string | null;
  // 공사 안내문 QR 로 왔을 때 그 현장. 간편접수만 채우고 손 등록은 비운다.
  source_project_id: string | null;
  // 공개 페이지로 왔을 때 그 링크(기본 목록이면 null)와 보고 있던 사례. 간편접수만 채운다.
  source_public_page_id: string | null;
  source_portfolio_id: string | null;
  // 소개 링크로 왔을 때 소개한 고객. 간편접수만 채운다.
  referred_by_customer_id: string | null;
  stage: CustomerStage;
  lost_reason: string | null;
  assignee_id: string | null;
  consult: ConsultNeed[];
  /** 화면에 열을 늘리지 않고 붙이는 자유 키. */
  meta: Record<string, string>;
  memo: string;
  // 첫 응대 시각 — 저장소가 한 번만 채운다(`markFirstContact` · 손 등록은 등록 시각).
  first_contacted_at: string | null;
}

/** 등록·편집이 건드리는 열만. id·org_id·stage 는 저장소가 정한다. */
export type CustomerInput = Omit<
  Customer,
  keyof OrgScopedRow | "stage" | "lost_reason" | "first_contacted_at"
>;
